import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import SsDefaultAssignee from '../../components/SsDefaultAssignee';
import toast from 'react-hot-toast';
import { Save, Upload } from 'lucide-react';
import { getSsHandbookInfo, formatSsTitle } from '../../data/ssHandbookInfo';

function SsHandbookBody({ info }) {
  const sections = Array.isArray(info.sections) ? info.sections : [];
  if (!sections.length) {
    return info.body ? <p>{info.body}</p> : null;
  }
  return (
    <div className="space-y-3">
      {sections.map((section) => (
        <section key={section.heading} className="space-y-1">
          <h3 className="text-sm font-semibold text-gray-900">{section.heading}</h3>
          {section.text ? <p>{section.text}</p> : null}
          {section.points ? (
            <ul className="list-disc pl-4 space-y-1">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          ) : null}
          {section.rows ? (
            <ul className="space-y-2">
              {section.rows.map((row) => (
                <li key={row.ss} className="rounded-lg border border-slate-200 px-2.5 py-2">
                  <p className="font-semibold text-gray-900">{row.ss}</p>
                  <p className="text-gray-700">{row.sign}</p>
                  <p className="text-gray-800">{row.wording}</p>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
    </div>
  );
}
import { formatUnitIdShort } from '../../utils/unitIdFormat';
import { confirmedChecklistCount } from '../../utils/unitDisplay';
import SsEquipmentTypePool from '../../components/SsEquipmentTypePool';

const FREQ = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SIX_MONTHLY', label: '6-monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const CS_FIELDS = [
  { key: 'location', label: 'Location', rows: 2 },
  { key: 'performance_standard', label: 'Performance standard', rows: 2 },
  { key: 'inspection_procedure', label: 'Inspection procedure', rows: 3 },
  { key: 'maintenance_procedure', label: 'Maintenance', rows: 3 },
  { key: 'reporting', label: 'Reporting', rows: 2 },
];

export default function BuildingSsInstanceDetail() {
  const { id: buildingId, instanceId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);

  const { data: building } = useQuery({
    queryKey: ['building', buildingId],
    queryFn: async () => (await api.get(`/buildings/${buildingId}`)).data,
    enabled: !!buildingId,
  });

  const { data: pack, isLoading, error } = useQuery({
    queryKey: ['ss-instance', buildingId, instanceId],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/ss-instances/${instanceId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!buildingId && !!instanceId,
  });

  const units = pack?.units || [];

  useEffect(() => {
    if (!pack) return;
    setForm({
      location: pack.location || '',
      performance_standard: pack.performance_standard || '',
      inspection_procedure: pack.inspection_procedure || '',
      maintenance_procedure: pack.maintenance_procedure || '',
      reporting: pack.reporting || '',
      inspection_frequency: pack.inspection_frequency || 'ANNUAL',
      documents_path: pack.documents_path || '',
    });
  }, [pack?.id]);

  const handbook = getSsHandbookInfo(pack?.ss_code);
  const title = formatSsTitle(pack?.ss_code, pack?.location || pack?.ss_name);

  const unitTypeGroups = useMemo(() => {
    const map = new Map();
    for (const u of units) {
      const t = String(u.unit_type || '').trim() || 'Unspecified';
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(u);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [units]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.put(`/buildings/${buildingId}/ss-instances/${instanceId}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Saved');
      queryClient.invalidateQueries(['ss-instance', buildingId, instanceId]);
      queryClient.invalidateQueries(['building', buildingId]);
      const idx = window.history.state?.idx;
      if (typeof idx === 'number' && idx > 0) navigate(-1);
      else navigate(`/buildings/${buildingId}`);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const uploadDoc = async (file) => {
    if (!file || !form) return;
    try {
      const fd = new FormData();
      fd.append('type', 'compliance-schedule');
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, {
        headers: { 'X-Upload-Type': 'compliance-schedule' },
      });
      const filename = res.data?.filename;
      if (!filename) throw new Error('No filename');
      setForm((p) => ({ ...p, documents_path: filename }));
      toast.success('Uploaded ?? click Save');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Upload failed');
    }
  };

  if (isLoading || !form) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="CS SS" />
        <p className="text-gray-500 mt-6">Loading…</p>
      </div>
    );
  }

  if (error || !pack) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="CS SS" />
        <p className="text-red-600 mt-6">{error?.response?.data?.error || 'Not found'}</p>
      </div>
    );
  }

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={building?.name}
        title={title}
        infoTitle={handbook?.title || title}
        infoChildren={
          <div className="space-y-2 text-sm">
            {building?.name ? <p className="font-medium">{building.name}</p> : null}
            {handbook ? <SsHandbookBody info={handbook} /> : null}
          </div>
        }
      />

      <div className="bg-card rounded-xl shadow p-4 space-y-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">Default IQP</p>
            <p className="text-xs text-gray-500">
              Who is responsible for this SS by default (cascades to units). Save here separately
              from CS text below.
            </p>
          </div>
          <SsDefaultAssignee
            buildingId={buildingId}
            instanceId={instanceId}
            ssCode={pack?.ss_code}
            instancePack={pack}
            onSaved={() => {
              queryClient.invalidateQueries(['ss-instance', buildingId, instanceId]);
              queryClient.invalidateQueries(['building', buildingId]);
            }}
          />
        </div>
        {(pack.default_assignee_name ||
          pack.default_worker_name ||
          pack.default_worker_id ||
          pack.partner_company_name) && (
          <p className="text-sm text-sky-900 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">
            Current:{' '}
            <strong>
              {pack.default_assignee_name ||
                pack.default_worker_name ||
                pack.partner_company_name ||
                `Worker #${pack.default_worker_id}`}
            </strong>
            {pack.default_assignee_type === 'EXTERNAL_COMPANY'
              ? ' · External'
              : pack.default_assignee_type === 'INTERNAL_IQP'
                ? ' · Internal IQP'
                : ''}
          </p>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate({ ...form });
        }}
        className="space-y-4"
      >
        <div className="bg-card rounded-xl shadow p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <select
              className="border rounded-lg px-3 py-2 text-sm"
              value={form.inspection_frequency}
              onChange={(e) => setForm((p) => ({ ...p, inspection_frequency: e.target.value }))}
            >
              {FREQ.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          {(pack.partner_phone || pack.partner_email || pack.partner_company_name) && (
            <p className="text-xs text-gray-600">
              {[pack.partner_company_name, pack.partner_email, pack.partner_phone]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}

          {CS_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
              <textarea
                rows={f.rows}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form[f.key] || ''}
                onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              />
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-2 border border-dashed rounded-lg text-xs cursor-pointer hover:bg-gray-50">
              <Upload className="w-3.5 h-3.5" />
              File
              <input
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  uploadDoc(file);
                }}
              />
            </label>
            {form.documents_path && (
              <span className="text-xs text-gray-500 truncate max-w-[12rem]">{form.documents_path}</span>
            )}
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="ml-auto inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-1.5" />
              Save
            </button>
          </div>
        </div>

        <div className="bg-card rounded-xl shadow p-4 space-y-3">
          <SsEquipmentTypePool
            buildingId={buildingId}
            instanceId={instanceId}
            ssCode={pack.ss_code}
          />
        </div>

        <div className="bg-card rounded-xl shadow p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">Types & units</h2>
          {unitTypeGroups.length === 0 ? (
            <p className="text-sm text-gray-500">No units yet.</p>
          ) : (
            unitTypeGroups.map(([typeLabel, list]) => (
              <div key={typeLabel} className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/buildings/${buildingId}/checklists?ssCode=${encodeURIComponent(pack.ss_code || '')}&unitType=${encodeURIComponent(
                        typeLabel === 'Unspecified' ? '' : typeLabel
                      )}`
                    )
                  }
                  className="w-full px-3 py-2 bg-slate-50 text-left text-sm font-semibold hover:bg-primary-50"
                >
                  {typeLabel} · {list.length}
                </button>
                <ul className="divide-y">
                  {list.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/buildings/${buildingId}/units/${u.id}`)}
                        className="w-full flex gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                      >
                        <span className="font-mono text-primary-700">
                          {formatUnitIdShort(u.unique_id, u.ss_code)}
                        </span>
                        <span className="flex-1 truncate text-gray-700">{u.location || u.name || '—'}</span>
                        <span className="text-xs text-gray-500">{confirmedChecklistCount(u)} Q</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </form>
    </div>
  );
}
